import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { useLanguage } from "../../i18n";

interface BroadcastTenant {
  tenancyId: string;
  tenantName: string;
  tenantPhone: string;
  propertyLabel: string;
}

// WhatsApp only has a real "send instantly to everyone" API through Meta's
// paid Business API (business verification, approved templates, per-message
// cost) — nothing this app has. So this composes one message, then walks
// the owner down their tenant list with a wa.me deep link per tenant (their
// own WhatsApp app does the actual sending) instead of them retyping the
// same message N times. "Sent" here just means the deep link was opened —
// there's no delivery receipt to confirm the owner didn't back out of it.
export function OwnerBroadcast({ onBack }: { onBack: () => void }) {
  const { t } = useLanguage();
  const [tenants, setTenants] = useState<BroadcastTenant[] | null>(null);
  const [message, setMessage] = useState("");
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.get("/api/broadcast/tenants").then(({ tenants }) => setTenants(tenants));
  }, []);

  function waLink(phone: string): string {
    return `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
  }

  function markSent(tenancyId: string) {
    setSentTo((prev) => new Set(prev).add(tenancyId));
  }

  const trimmed = message.trim();

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0 }}>{t("ownerBroadcast.title")}</div>
      </div>

      <div className="field">
        <label>{t("ownerBroadcast.messageLabel")}</label>
        <div className="input-wrap">
          <textarea
            rows={4}
            placeholder={t("ownerBroadcast.messagePlaceholder")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div className="field-hint">{t("ownerBroadcast.messageHint")}</div>
      </div>

      {!tenants ? (
        <div className="spinner" />
      ) : tenants.length === 0 ? (
        <div className="empty-state">{t("ownerBroadcast.emptyState")}</div>
      ) : (
        <>
          <div className="section-title" style={{ fontSize: 12, marginTop: 20 }}>
            {t("ownerBroadcast.recipientsHeading", { count: tenants.length })}
          </div>
          {tenants.map((tn) => {
            const isSent = sentTo.has(tn.tenancyId);
            return (
              <div className="card-row" key={tn.tenancyId} style={{ padding: "10px 0" }}>
                <div style={{ minWidth: 0 }}>
                  <b style={{ fontSize: 13.5 }}>{tn.tenantName}</b>
                  <div className="sub">{tn.propertyLabel} · {tn.tenantPhone}</div>
                </div>
                <a
                  className={isSent ? "btn-outline btn-sm" : "btn-solid btn-sm"}
                  style={{ flexShrink: 0, textDecoration: "none", opacity: !trimmed ? 0.5 : 1, pointerEvents: !trimmed ? "none" : "auto" }}
                  href={trimmed ? waLink(tn.tenantPhone) : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trimmed && markSent(tn.tenancyId)}
                >
                  {isSent ? t("ownerBroadcast.sentBtn") : t("ownerBroadcast.sendBtn")}
                </a>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
